import { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Bell, Check, CheckCheck, Briefcase, StickyNote, ClipboardCheck, Route, MessageSquare, Loader2, Megaphone } from "lucide-react";
import { Button } from "../components/ui/button";
import PageHeader from "../components/PageHeader";

const CATEGORY_ICONS = {
  services: Briefcase,
  notes: StickyNote,
  audits: ClipboardCheck,
  accompagnements: Route,
  messages: MessageSquare,
  marketing: Megaphone,
};

const CATEGORY_COLORS = {
  services: "#3b82f6",
  notes: "#f59e0b",
  audits: "#10b981",
  accompagnements: "#8b5cf6",
  messages: "#f7c935",
  marketing: "#10b981",
};

export default function NotificationsPage() {
  const { axiosAuth } = useAuth();
  const api = axiosAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const r = await api.get("/notifications");
      setNotifications(r.data);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const markAllRead = async () => {
    await api.put("/notifications/read", { all: true });
    load();
    window.dispatchEvent(new Event("blitzz:notifs-updated"));
  };

  const markOneRead = async (id) => {
    await api.put(`/notifications/${id}/read`);
    load();
    window.dispatchEvent(new Event("blitzz:notifs-updated"));
  };

  const formatDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const now = new Date();
    const diff = (now - d) / 1000;
    if (diff < 60) return "A l'instant";
    if (diff < 3600) return `Il y a ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `Il y a ${Math.floor(diff / 3600)}h`;
    return d.toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  if (loading) return <div className="p-8 text-slate-400">Chargement...</div>;

  return (
    <div className="p-8 max-w-3xl" data-testid="notifications-page">
      <PageHeader
        icon={<Bell />}
        title="Notifications"
        description={unreadCount > 0 ? `${unreadCount} non lue${unreadCount > 1 ? "s" : ""}` : "Tout est à jour"}
        testId="notifications-header"
        actions={unreadCount > 0 ? (
          <Button onClick={markAllRead} variant="outline" className="border-white/10 text-white hover:bg-white/5" data-testid="mark-all-read-btn">
            <CheckCheck className="w-4 h-4 mr-2" /> Tout marquer comme lu
          </Button>
        ) : null}
      />

      <div className="space-y-2">
        {notifications.length === 0 ? (
          <div className="bg-[#14101c] border border-white/10 rounded-xl p-12 text-center">
            <Bell className="w-10 h-10 mx-auto text-slate-600 mb-3" />
            <p className="text-slate-500">Aucune notification</p>
          </div>
        ) : notifications.map((n) => {
          const Icon = CATEGORY_ICONS[n.category] || Bell;
          const color = CATEGORY_COLORS[n.category] || "#f7c935";
          return (
            <div
              key={n.notification_id}
              data-testid={`notif-${n.notification_id}`}
              onClick={() => {
                if (!n.read) markOneRead(n.notification_id);
                if (n.link) navigate(n.link);
              }}
              className={`bg-[#14101c] border rounded-xl p-4 flex items-start gap-4 cursor-pointer transition-colors hover:border-[#f7c935]/30 ${
                n.read ? "border-white/5 opacity-60" : "border-white/10"
              }`}
            >
              <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${color}15`, color }}>
                <Icon className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-sm">{n.title}</span>
                  {!n.read && <span className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0" />}
                </div>
                <p className="text-sm text-slate-400 mt-0.5 truncate">{n.message}</p>
                <p className="text-xs text-slate-600 mt-1">{formatDate(n.created_at)}</p>
              </div>
              {!n.read && (
                <button onClick={(e) => { e.stopPropagation(); markOneRead(n.notification_id); }}
                  className="p-1.5 rounded hover:bg-white/10 text-slate-500 hover:text-white transition-colors flex-shrink-0">
                  <Check className="w-4 h-4" />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
